/**
 * Theme controller (M-09).
 *
 * Tailwind's `dark:` utilities trigger when the `dark` class sits on
 * <html>. This module is the single source of truth for adding /
 * removing that class and persisting the user's choice.
 *
 * Behaviour:
 * - First load: read `cutmate:theme` from localStorage; fall back to
 *   `prefers-color-scheme: dark` on the OS.
 * - `setTheme("dark" | "light")` flips the class and persists.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "cutmate:theme";

export function getStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "dark" || raw === "light" ? raw : null;
}

export function getSystemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function getInitialTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // private browsing
  }
}

/** Apply the initial theme at module load — before React mounts. */
export function initTheme(): Theme {
  const theme = getInitialTheme();
  applyTheme(theme);
  return theme;
}
